import { PencilIcon } from "@vericus/cadmus-icons";
import { Button, colors, IconButton, Text } from "@vericus/cadmus-ui";

import {
  MEMBERS_COLUMN_LABEL,
  PeerOutcomeTable,
  STATUS_COLUMN_LABEL,
} from "../utils/utils";
import * as styles from "./peer-overview-table.css";

interface Props {
  /** Criteria headers */
  headers: { id: string; title: string }[];
  /** Filter state for pending marks */
  filterByPending: boolean;
  /** Rows of the table */
  rows: PeerOutcomeTable;
  /** Group Member user IDs that have not yet been marked by the current student */
  pendingMarkIds: string[];
  /** Current student */
  me: { id: string; name: string };
  /** Hide per-row Edit button when peer eval is past due. */
  disableEdit?: boolean;
  /** Callback to trigger a Mark action on a row/student */
  onMarkStudent: (studentId: string) => void;
  /** Callback to trigger an Edit action on a completed row/student */
  onEditStudent: (studentId: string) => void;
}

/**
 * Table with Student peers as rows, and rubric Criteria as columns, with the
 * cells displaying the Criterion selection made by the current student marker.
 */
export function PeerOverviewTable({
  me,
  headers,
  rows,
  filterByPending,
  pendingMarkIds,
  disableEdit = false,
  onMarkStudent,
  onEditStudent,
}: Props) {
  const selfStyle = { background: colors.bgrey300 };

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        {headers && (
          <thead>
            <tr>
              <th className={styles.tableHeader}>
                <Text kind="bodyLine" bold>
                  {MEMBERS_COLUMN_LABEL}
                </Text>
              </th>
              {headers.map((header, key) => (
                <th key={header.id} className={styles.tableHeader}>
                  <Text kind="headingSix">{`Criteria ${key + 1}`}</Text>
                  {header.title && <Text kind="bodyLine">{header.title}</Text>}
                </th>
              ))}
              <th className={styles.tableHeader}>
                <Text kind="bodyLine" bold>
                  {STATUS_COLUMN_LABEL}
                </Text>
              </th>
            </tr>
          </thead>
        )}

        <tbody>
          {rows.map((row) => {
            if (filterByPending && !pendingMarkIds.includes(row.userId)) {
              return null;
            }

            return (
              <tr key={`peer-row-${row.userId}`}>
                <td
                  key={`column-student-student-${row.userId}`}
                  className={styles.column}
                  style={me.id === row.userId ? selfStyle : {}}
                >
                  <Text kind="bodyLine" bold>
                    {me.id === row.userId ? `${row.userName} (Self Eval)` : row.userName}
                  </Text>
                </td>
                {row?.outcomes?.map((outcome) => (
                  <td
                    key={`column-${outcome.criteriaId}-student-${row.userId}`}
                    className={styles.column}
                    style={me.id === row.userId ? selfStyle : {}}
                  >
                    <Text kind="bodyLine">{outcome.scaleValue}</Text>
                  </td>
                ))}
                <td
                  key={`column-status-student-${row.userId}`}
                  className={styles.columnStatus}
                >
                  {pendingMarkIds.includes(row.userId) ? (
                    <Button onClick={() => onMarkStudent(row.userId)}>
                      Mark
                    </Button>
                  ) : (
                    <div className={styles.statusCellContent}>
                      <Text kind="bodyLine" color="success">
                        Done
                      </Text>
                      {!disableEdit && (
                        <IconButton
                          shape="square"
                          outline
                          aria-label={`Edit evaluation for ${row.userName}`}
                          onClick={() => onEditStudent(row.userId)}
                        >
                          <PencilIcon label="" size={16} />
                        </IconButton>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
